import React from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { formatCurrency } from 'utils';
import { confidenceColor } from '../lib/fieldGroups';
import { DetailRow } from './DetailRow';

const confidenceLabel = (score: number): string => {
  if (score >= 0.85) return 'High';
  if (score >= 0.65) return 'Medium';
  return 'Low';
};

// Shared "matched record" card used by both the EXISTING_OPPORTUNITY_FOUND and
// EXISTING_SUBMISSION_FOUND confirm modals — same fields, different section label/link target.
interface Props {
  sectionLabel: string;
  matchConfidence: number | null;
  linkUrl: string | null;
  applicantName: string | null;
  contactName: string | null;
  equipmentDescription: string | null;
  requestedAmount: number | null;
  dealerName: string | null;
}

export const MatchedRecordDetailsCard = ({
  sectionLabel,
  matchConfidence,
  linkUrl,
  applicantName,
  contactName,
  equipmentDescription,
  requestedAmount,
  dealerName,
}: Props) => (
  <Box>
    <Typography component="div" variant="subtitle2" sx={{ mb: 1 }}>
      {sectionLabel}
    </Typography>
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', p: 2 }}>
      <DetailRow label="Match Confidence:">
        {matchConfidence != null ? (
          <Typography component="span" variant="subtitle2" sx={{ color: confidenceColor(matchConfidence) }}>
            {Math.round(matchConfidence * 100)}% - {confidenceLabel(matchConfidence)}
          </Typography>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </DetailRow>
      <DetailRow label="Applicant / Business Name:">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2">{applicantName || '—'}</Typography>
          {linkUrl && (
            <IconButton size="small" component="a" href={linkUrl} target="_blank" rel="noopener noreferrer">
              <OpenInNewIcon fontSize="small" color="primary" />
            </IconButton>
          )}
        </Stack>
      </DetailRow>
      <DetailRow label="Contact:">
        <Typography variant="body2">{contactName || '—'}</Typography>
      </DetailRow>
      <DetailRow label="Equipment:">
        <Typography variant="body2">{equipmentDescription || '—'}</Typography>
      </DetailRow>
      <DetailRow label="Requested Amount:">
        <Typography variant="body2">{requestedAmount != null ? formatCurrency(requestedAmount) : '—'}</Typography>
      </DetailRow>
      <DetailRow label="Dealer:">
        <Typography variant="body2">{dealerName || '—'}</Typography>
      </DetailRow>
    </Box>
  </Box>
);
